
import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'
import { Task } from 'types'

type Vars = {
  task: Task
  files: File[]
  extraFormData?: Record<string, string | number | boolean>
}

type Ctx = { prevTask: Task; tempUrls: string[] }
type UploadTaskImagesResponse = { urls: string[] } // your endpoint returns only new URLs

async function uploadTaskImagesApi({ task, files, extraFormData }: Vars): Promise<string[]> {
  const form = new FormData()
  files.forEach(f => form.append('files', f))
  form.append('tableName', 'TASKS_TABLE')
  form.append('PK', task.PK)
  form.append('SK', task.SK)
  form.append('fieldName', 'imageUrls')
  if (extraFormData) {
    Object.entries(extraFormData).forEach(([k, v]) => form.append(k, String(v)))
  }

  const res = await axios.post<UploadTaskImagesResponse>(`${baseUrl}/aws/s3/upload`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data?.urls ?? []
}

/** Update all caches that might contain this task. Adjust keys to your app. */
function syncTaskEverywhere(qc: QueryClient, next: Task) {
  // 1) single-task cache
  qc.setQueryData(['task', next.PK, next.SK], next)

  // 2) list caches you render from
  // Example A: a flat list of tasks
  qc.setQueryData(['tasks_table'], (old: Task[] | undefined) => {
    if (!Array.isArray(old)) return old
    return old.map(t => (t.SK === next.SK ? { ...t, ...next } : t))
  })

  // Example B: your entities aggregator (if used)
  qc.setQueryData(['entitiesFromAllItems'], (old: any) => {
    if (!old || !Array.isArray(old.allTasks)) return old
    const updatedTasks = old.allTasks.map((t: Task) => (t.SK === next.SK ? { ...t, ...next } : t))
    return { ...old, allTasks: updatedTasks }
  })
}

export default function useUploadTaskImages() {
  const queryClient = useQueryClient()

  return useMutation<string[], Error, Vars, Ctx>({
    mutationFn: uploadTaskImagesApi,

    // OPTIMISTIC: add temp blob URLs immediately
    onMutate: async ({ task, files }): Promise<Ctx> => {
      await queryClient.cancelQueries({})

      // freshest base we have
      const base = (queryClient.getQueryData(['task', task.PK, task.SK]) as Task | undefined) ?? task
      const tempUrls = files.map((f: any) => f.previewUrl || URL.createObjectURL(f))

      const next: Task = {
        ...base,
        imageUrls: Array.from(new Set([...(base.imageUrls || []), ...tempUrls])),
      }

      syncTaskEverywhere(queryClient, next)
      return { prevTask: base, tempUrls }
    },

    // ROLLBACK if server fails
    onError: (_err, { task }, ctx) => {
      if (!ctx) return
      syncTaskEverywhere(queryClient, ctx.prevTask)
    },

    // SUCCESS: swap temps -> final S3 URLs
    onSuccess: (finalUrls, { task }, ctx) => {
      const base = (queryClient.getQueryData(['task', task.PK, task.SK]) as Task | undefined) ?? task

      // remove temps, add finals
      const withoutTemps = (base.imageUrls || []).filter(u => !ctx.tempUrls.includes(u))
      const next: Task = {
        ...base,
        imageUrls: Array.from(new Set([...withoutTemps, ...finalUrls])),
      }

      syncTaskEverywhere(queryClient, next)
    },

    // Keep data fresh regardless
    onSettled: (_res, _err, { task }) => {
      queryClient.invalidateQueries({ queryKey: ['task', task.PK, task.SK] })
      // If your all-items aggregator is query-driven (not only setQueryData),
      // also invalidate that key:
      // queryClient.invalidateQueries({ queryKey: ['entitiesFromAllItems'] })
    },
  })
}
